import React, { useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ChevronLeft, ChevronRight, ChevronsDown, ChevronsUp } from 'lucide-react';
import TaskCard from './TaskCard';
import { BOARD_COLUMNS } from '../utils/taskConstants';

const PAGE_SIZE = 5;

export default function Board({ tasks, onTaskClick, onTaskMoved, canDrag, isGuest }) {
  const columns = useMemo(() => (isGuest ? BOARD_COLUMNS.filter((col) => col.id !== 'done') : BOARD_COLUMNS), [isGuest]);

  const grouped = useMemo(() => {
    const map = {};
    columns.forEach((col) => {
      map[col.id] = [];
    });
    tasks.forEach((task) => {
      if (map[task.status]) {
        map[task.status].push(task);
      }
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)));
    return map;
  }, [tasks, columns]);

  const [pages, setPages] = useState({});
  const [collapsed, setCollapsed] = useState(() => ({ done: false, cancelled: true }));

  const ensureValidPage = (columnId, totalItems) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
    const current = pages[columnId] || 1;
    return Math.min(current, totalPages);
  };

  const changePage = (columnId, page, totalItems) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
    const next = Math.min(Math.max(1, page), totalPages);
    setPages((prev) => ({ ...prev, [columnId]: next }));
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !canDrag) return;
    const taskId = parseInt(result.draggableId.replace('task-', ''), 10);
    const destinationColumn = result.destination.droppableId;
    const destinationPage = pages[destinationColumn] || 1;
    const absoluteIndex = (destinationPage - 1) * PAGE_SIZE + result.destination.index;
    onTaskMoved(taskId, destinationColumn, absoluteIndex);
  };

  return (
    <div className="board-wrapper">
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="board">
          {columns.map((column) => {
            const columnTasks = grouped[column.id] || [];
            const totalPages = Math.max(1, Math.ceil(columnTasks.length / PAGE_SIZE));
            const currentPage = ensureValidPage(column.id, columnTasks.length);
            const pageTasks = columnTasks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
            const isCollapsed = collapsed[column.id] ?? column.defaultCollapsed ?? false;

            return (
              <Column
                key={column.id}
                column={column}
                tasks={pageTasks}
                totalTasks={columnTasks.length}
                currentPage={currentPage}
                totalPages={totalPages}
                collapsed={isCollapsed}
                onToggleCollapse={() => setCollapsed((prev) => ({ ...prev, [column.id]: !isCollapsed }))}
                onTaskClick={onTaskClick}
                onTaskMoved={onTaskMoved}
                onPageChange={(page) => changePage(column.id, page, columnTasks.length)}
                canDrag={canDrag && !isCollapsed}
              />
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}

function Column({
  column,
  tasks,
  totalTasks,
  currentPage,
  totalPages,
  collapsed,
  onToggleCollapse,
  onTaskClick,
  onTaskMoved,
  onPageChange,
  canDrag,
}) {
  return (
    <div className={`column ${collapsed ? 'column-collapsed' : ''}`} data-status={column.id}>
      <div className="column-header">
        <div className="column-title-wrap">
          <div className="column-title">
            <span className="column-dot" style={{ background: column.color }} />
            {column.title}
          </div>
          <span className="column-count">{totalTasks}</span>
        </div>

        <div className="column-header-actions">
          {column.collapsible && (
            <button className="column-toggle-btn" onClick={onToggleCollapse} type="button" title={collapsed ? 'Mở rộng cột' : 'Thu gọn cột'}>
              {collapsed ? <ChevronsDown size={15} /> : <ChevronsUp size={15} />}
            </button>
          )}
        </div>
      </div>

      {!collapsed && (
        <>
          <Droppable droppableId={column.id} isDropDisabled={!canDrag}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="column-body"
                style={snapshot.isDraggingOver ? { background: 'var(--accent-glow)', borderRadius: 12 } : undefined}
              >
                {tasks.length === 0 && !snapshot.isDraggingOver && <div className="empty-state">Không có task nào</div>}
                {tasks.map((task, index) => (
                  <Draggable
                    key={task.id}
                    draggableId={`task-${task.id}`}
                    index={index}
                    isDragDisabled={!canDrag || !task.can_quick_move}
                  >
                    {(dragProvided, dragSnapshot) => (
                      <div ref={dragProvided.innerRef} {...dragProvided.draggableProps} {...dragProvided.dragHandleProps}>
                        <TaskCard
                          task={task}
                          onClick={() => task.can_open && onTaskClick(task)}
                          isDragging={dragSnapshot.isDragging}
                          isDisabled={!task.can_open}
                          onQuickMove={(nextStatus) => onTaskMoved(task.id, nextStatus, null)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>

          <div className="column-pagination">
            <button className="pagination-btn" disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)} type="button">
              <ChevronLeft size={14} />
            </button>
            <span className="column-page-indicator">{currentPage}/{totalPages}</span>
            <button className="pagination-btn" disabled={currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)} type="button">
              <ChevronRight size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
